/**
 * Commands of the easing feature (W2-05; WORKPACKAGES Appendix B: `easing.*`, `templates.*`):
 *  - `easing.copy` / `easing.paste`: copy the curve of the primary selected segment, paste it on every selected
 *    segment (one transaction; segments that refuse it are skipped and counted, e.g. a Bezier on scroll speed);
 *  - `easing.set.<n>`: palette entries "Ease: Soft, slow end (OutQuad, 4)" for every curve of the picker;
 *  - `easing.bezier`: the Bezier editor for the selected segments;
 *  - `easing.clearRecents`;
 *  - `templates.open` and `templates.<id>`: the motion templates dialog for the active line at the playhead.
 * The picker itself opens from the motion columns' `segments.easing` (W2-04a), the inspector and the curve editor.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { CommandContext, CommandDef, MenuPlacement } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import { buildTempoMap } from '../../core/doc/container';
import { Ease, HOLD, LINEAR } from '../../core/ease/index';
import type { BezierPoints, Ease as EaseValue } from '../../core/ease/types';
import type { KeyId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import { TEMPLATE_IDS } from '../../core/tools/templates';
import type { TemplateId } from '../../core/tools/types';
import type { MessageRef } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { m } from './easing.messages';
import { applyCount, bezierAllowed, cellById, RECENTS_KEY, segmentInfos } from './pickerModel';
import { m as tm } from './templates.messages';

type Ctx = CommandContext;

// ---------------------------------------------------------------------------------------------------------------
// Selection helpers
// ---------------------------------------------------------------------------------------------------------------

/** Selected segments (start keys that still start a segment), in selection order. */
export function selectedSegments(ctx: Pick<Ctx, 'store' | 'editor'>): KeyId[] {
  const store = ctx.store;
  if (store === null) return [];
  const out: KeyId[] = [];
  for (const id of ctx.editor.getState().selection.segments) {
    if (store.doc.keys.has(id) && store.idx.nextKey(id) !== undefined) out.push(id);
  }
  return out;
}

/** The segment whose curve `easing.copy` copies: the primary selected segment, else the first one. */
export function primarySegment(ctx: Pick<Ctx, 'store' | 'editor'>): KeyId | null {
  const segs = selectedSegments(ctx);
  const primary = ctx.editor.getState().selection.primary;
  if (primary?.kind === 'segment' && segs.includes(primary.id as KeyId)) return primary.id as KeyId;
  return segs[0] ?? null;
}

function needsSegments(ctx: Ctx): true | MessageRef {
  if (ctx.store === null) return { key: m.noProject };
  return selectedSegments(ctx).length > 0 ? true : { key: m.noSegments };
}

/** Runs `Core.keys.setEase` on segments as one transaction and reports the result in a toast. */
export function applyEase(
  ctx: Ctx,
  segments: readonly KeyId[],
  ease: EaseValue,
  label: MessageRef,
  command: string,
): boolean {
  const store = ctx.store;
  if (store === null) return false;
  const out = store.transact({ label, command }, (tx) => Core.keys.setEase(tx, { segments, ease }));
  if (!out.ok) {
    ctx.ui.toast(out.reason, { kind: 'error' });
    return false;
  }
  const applied = segments.length - out.value.skipped;
  ctx.ui.toast(
    { key: command === 'easing.paste' ? m.pastedCurve : m.setCurve, params: { count: applied } },
    { kind: 'success', undo: true },
  );
  for (const note of out.notes) ctx.ui.toast(note, { kind: 'info' });
  return true;
}

/**
 * `applyEase` on the selected segments that take `ease` at the editor level, with a toast counting the ones skipped.
 * The Guided level's speed rule (only Linear and Stay on scroll speed, DESIGN.md §4.12) is a UI rule: `Core.keys.setEase`
 * allows eased legacy speed, so every command that sets a curve filters through `segmentInfos` first.
 */
export function applyEaseGuarded(ctx: Ctx, ease: EaseValue, label: MessageRef, command: string): boolean {
  const store = ctx.store;
  if (store === null) return false;
  const segments = selectedSegments(ctx);
  const infos = segmentInfos(store.doc, store.idx, segments, ctx.editor.getState().level);
  const allowed = infos.filter((i) => Ease.validateFor(ease, i.use) === null).map((i) => i.key);
  if (allowed.length === 0) {
    const count = applyCount(infos, ease);
    ctx.ui.toast(count.reason ?? { key: m.noSegments }, { kind: 'warning' });
    return false;
  }
  if (allowed.length < segments.length) {
    ctx.ui.toast(
      {
        key: m.skipped,
        params: {
          skipped: segments.length - allowed.length,
          count: segments.length,
          reason: ctx.i18n.tr(applyCount(infos, ease).reason ?? { key: m.noSegments }),
        },
      },
      { kind: 'info' },
    );
  }
  return applyEase(ctx, allowed, ease, label, command);
}

/** `enabled` of a command that sets `ease` on the selected segments: at least one of them takes it. */
function canApply(ctx: Ctx, ease: EaseValue): true | MessageRef {
  const ok = needsSegments(ctx);
  if (ok !== true || ctx.store === null) return ok;
  const infos = segmentInfos(ctx.store.doc, ctx.store.idx, selectedSegments(ctx), ctx.editor.getState().level);
  const count = applyCount(infos, ease);
  return count.applies > 0 ? true : (count.reason ?? { key: m.noSegments });
}

// ---------------------------------------------------------------------------------------------------------------
// Curve clipboard
// ---------------------------------------------------------------------------------------------------------------

let copiedEase: EaseValue | null = null;

/** The curve `easing.copy` copied (null: none yet). */
export function copiedCurve(): EaseValue | null {
  return copiedEase;
}

export function setCopiedCurve(e: EaseValue | null): void {
  copiedEase = e;
}

function easeLabel(ctx: Ctx, e: EaseValue): string {
  switch (e.kind) {
    case 'hold':
      return ctx.i18n.t(m.hold);
    case 'bezier':
      return `${ctx.i18n.t(m.tabBezier)} (${e.p.join(', ')})`;
    case 'tween': {
      const entry = Ease.catalog(e.id);
      const base = entry === null ? String(e.id) : `${entry.rpeName} (${String(e.id)})`;
      return e.left === 0 && e.right === 1 ? base : `${base} [${e.left.toFixed(2)}, ${e.right.toFixed(2)}]`;
    }
  }
}

const SEGMENT_MENUS: readonly MenuPlacement[] = [
  { menu: 'motion.context', group: 'curve', order: 40 },
  { menu: 'curve.context', group: 'curve', order: 40 },
];

const COPY: CommandDef = {
  id: 'easing.copy',
  title: m.cmdCopy,
  category: 'Motion',
  icon: 'Copy',
  level: 'guided',
  aliases: ['copy easing', 'copy ease', 'copy curve', '复制缓动'],
  when: 'sel.segments',
  menus: SEGMENT_MENUS.map((p) => ({ ...p, order: 41 })),
  enabled: needsSegments,
  run(ctx) {
    const id = primarySegment(ctx);
    const e = id === null ? undefined : ctx.store?.doc.keys.get(id)?.ease;
    if (e === undefined) {
      ctx.ui.toast({ key: m.noSegments }, { kind: 'warning' });
      return;
    }
    setCopiedCurve(e);
    ctx.ui.toast({ key: m.copiedCurve, params: { name: easeLabel(ctx, e) } }, { kind: 'info' });
  },
};

const PASTE: CommandDef = {
  id: 'easing.paste',
  title: m.cmdPaste,
  category: 'Motion',
  icon: 'ClipboardPaste',
  level: 'guided',
  aliases: ['paste easing', 'paste ease', 'paste curve', '粘贴缓动'],
  when: 'sel.segments',
  menus: SEGMENT_MENUS.map((p) => ({ ...p, order: 42 })),
  enabled(ctx) {
    if (copiedEase === null) return { key: m.noCurveCopied };
    return canApply(ctx, copiedEase);
  },
  run(ctx) {
    const e = copiedEase;
    if (e === null) return;
    applyEaseGuarded(ctx, e, { key: m.txPasteCurve }, 'easing.paste');
  },
};

function initialBezier(e: EaseValue | undefined): BezierPoints {
  if (e?.kind === 'bezier') return e.p;
  return [0.25, 0.1, 0.25, 1];
}

const BEZIER: CommandDef = {
  id: 'easing.bezier',
  title: m.cmdBezier,
  category: 'Motion',
  icon: 'Spline',
  level: 'full',
  aliases: ['bezier', 'custom curve', 'cubic-bezier', '贝塞尔', 'custom easing'],
  when: 'sel.segments',
  menus: SEGMENT_MENUS.map((p) => ({ ...p, order: 43 })),
  enabled(ctx) {
    const ok = needsSegments(ctx);
    if (ok !== true || ctx.store === null) return ok;
    const infos = segmentInfos(ctx.store.doc, ctx.store.idx, selectedSegments(ctx), ctx.editor.getState().level);
    if (infos.some((i) => bezierAllowed(i.use))) return true;
    return applyCount(infos, { kind: 'bezier', p: [0.25, 0.1, 0.25, 1] }).reason ?? { key: m.noSegments };
  },
  async run(ctx) {
    const segments = selectedSegments(ctx);
    const first = segments[0];
    if (first === undefined || ctx.store === null) return;
    const p = await ctx.ui.openDialog(DIALOG.bezierEditor, {
      initial: initialBezier(ctx.store.doc.keys.get(first)?.ease),
      segments,
    });
    if (p === null) return;
    applyEase(ctx, segments, { kind: 'bezier', p }, { key: m.txBezier }, 'easing.bezier');
  },
};

/** Ease and title of each "Ease: ..." palette command. */
const SET_TARGETS: ReadonlyArray<{ readonly suffix: string; readonly ease: () => EaseValue }> = [
  { suffix: 'hold', ease: () => HOLD },
  { suffix: '1', ease: () => LINEAR },
  ...Array.from({ length: 28 }, (_, i) => {
    const id = i + 2;
    return { suffix: String(id), ease: (): EaseValue => ({ kind: 'tween', id, left: 0, right: 1 }) };
  }),
  { suffix: 'quintInOut', ease: () => cellById('approx:quint')?.ease ?? LINEAR },
  { suffix: 'expoInOut', ease: () => cellById('approx:expo')?.ease ?? LINEAR },
];

function setCommand(target: (typeof SET_TARGETS)[number]): CommandDef {
  const e = target.ease();
  const entry = e.kind === 'tween' ? Ease.catalog(e.id) : null;
  return {
    id: `easing.set.${target.suffix}`,
    title: `easing.set.${target.suffix}` as MessageKey,
    category: 'Motion',
    icon: 'Spline',
    level: e.kind === 'bezier' ? 'full' : 'guided',
    aliases: [
      ...(entry?.aliases ?? []),
      ...(entry === null ? [] : [entry.rpeName, `ease ${entry.rpeName}`]),
      target.suffix === 'hold' ? 'stay' : '',
      'ease',
      '缓动',
    ].filter((a) => a !== ''),
    when: 'sel.segments',
    enabled: (ctx) => canApply(ctx, target.ease()),
    run(ctx) {
      const ease = target.ease();
      applyEaseGuarded(
        ctx,
        ease,
        { key: m.txSetCurve, params: { name: easeLabel(ctx, ease) } },
        `easing.set.${target.suffix}`,
      );
    },
  };
}

const CLEAR_RECENTS: CommandDef = {
  id: 'easing.clearRecents',
  title: m.cmdClearRecents,
  category: 'Motion',
  icon: 'ListX',
  level: 'guided',
  aliases: ['recent curves', 'clear easing history'],
  run(ctx) {
    ctx.settings.setExt(RECENTS_KEY, []);
    ctx.ui.toast({ key: m.recentsCleared }, { kind: 'info' });
  },
};

// ---------------------------------------------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------------------------------------------

/** The beat templates start at: the playhead, snapped to the beat grid. */
export function playheadBeat(ctx: Pick<Ctx, 'store' | 'editor' | 'audio'>): Beat {
  const store = ctx.store;
  if (store === null) return Beats.ZERO;
  const tempo = buildTempoMap(store.doc, store.idx);
  const sec = ctx.audio.transport.chartTime();
  const div = ctx.editor.getState().snap.div;
  const b = Beats.snap(tempo.beatAt(sec, Math.max(1, div)), Math.max(1, div));
  return Beats.cmp(b, Beats.ZERO) < 0 ? Beats.ZERO : b;
}

function needsLine(ctx: Ctx): true | MessageRef {
  if (ctx.store === null) return { key: m.noProject };
  const line = ctx.editor.getState().activeLine;
  return line !== null && ctx.store.doc.lines.has(line) ? true : { key: m.noActiveLine };
}

/** The generator side sheets of the batch module (closed before the templates sheet opens). */
const TOOL_SHEETS = [
  DIALOG.distribute,
  DIALOG.clone,
  DIALOG.fill,
  DIALOG.motionPath,
  DIALOG.bake,
  DIALOG.flatten,
] as const;

async function openTemplates(ctx: Ctx, initial?: TemplateId): Promise<void> {
  const line = ctx.editor.getState().activeLine;
  if (line === null || ctx.store === null) return;
  // One tool sheet at a time (UX-038): the generator sheets close (their previews revert) first.
  for (const id of TOOL_SHEETS) ctx.ui.closeDialog(id);
  // The dialog applies the template itself (one undoable step) and reports it.
  await ctx.ui.openDialog(DIALOG.templates, {
    line,
    at: playheadBeat(ctx),
    ...(initial === undefined ? {} : { initial }),
  });
}

const TEMPLATES_OPEN: CommandDef = {
  id: 'templates.open',
  title: tm.cmdOpen,
  category: 'Motion',
  icon: 'WandSparkles',
  level: 'guided',
  aliases: [
    'templates',
    'motion templates',
    'presets',
    'animate',
    'fade',
    'slide',
    'spin',
    'shake',
    '模板',
    '动画模板',
  ],
  hint: 2,
  menus: [
    { menu: 'tools.menu', group: 'motion', order: 10 },
    { menu: 'motion.context', group: 'templates', order: 10 },
    { menu: 'preview.context', group: 'templates', order: 10 },
  ],
  enabled: needsLine,
  async run(ctx) {
    await openTemplates(ctx);
  },
};

function templateCommand(id: TemplateId): CommandDef {
  return {
    id: `templates.${id}`,
    title: `templates.cmd.${id}` as MessageKey,
    category: 'Motion',
    icon: 'WandSparkles',
    level: 'guided',
    aliases: [id, 'template'],
    enabled: needsLine,
    async run(ctx) {
      await openTemplates(ctx, id);
    },
  };
}

export const EASING_COMMANDS: readonly CommandDef[] = [
  COPY,
  PASTE,
  BEZIER,
  CLEAR_RECENTS,
  ...SET_TARGETS.map(setCommand),
  TEMPLATES_OPEN,
  ...TEMPLATE_IDS.map(templateCommand),
];
